/**
 * 解题相关API
 * 包含解题分析和讲解视频生成功能
 */

import Logger from "../utils/logger";
import { API_PATHS } from "./config";
import {
  convertImageToBase64,
  getCurrentUser,
  requestManager,
  getCustomClientSlug,
  requestModelByModeConfig,
} from "./utils";
import { extractManimCode, escapeUnderscoreInLaTeXText } from "./manimApi";

/**
 * 锤锤解题模式 - 第一阶段：解题分析
 * @param {string} prompt 用户输入的题目描述
 * @param {Function} onChunkReceived 接收流式响应的回调函数
 * @param {Function} onComplete 完成时的回调函数
 * @param {Function} onError 错误处理回调函数
 * @param {File|File[]} imageFiles 可选的题目图片文件（最多3张）
 * @param {boolean} freeMode 是否使用免费模型与免费渠道
 */
export const streamSolveQuestion = async (
  prompt,
  onChunkReceived,
  onComplete,
  onError,
  imageFiles,
  freeMode = false
) => {
  const imageFilesArr = Array.isArray(imageFiles) ? imageFiles : (imageFiles ? [imageFiles] : []);
  Logger.info("开始锤锤解题分析", { prompt, imageCount: imageFilesArr.length });

  // 将API请求包装到队列中执行
  try {
    await requestManager.enqueue(async () => {
      try {
        // 构建简化的请求体 - 系统提示词和模型参数已移至后端
        const requestBody = {
          mode: "solve", // 数学解题模式
          prompt: prompt, // 用户的请求内容
          stream: false, // 改为非流式响应
          free_mode: freeMode,
        };
        const slug = getCustomClientSlug();
        if (slug) requestBody.custom_client_slug = slug;

        // 如果有图片，将所有图片转为 base64 数组
        if (imageFilesArr.length > 0) {
          const base64Images = await Promise.all(imageFilesArr.map(convertImageToBase64));
          requestBody.images = base64Images;
        }

        Logger.debug("发送解题分析API请求", requestBody);

        // 添加用户ID到请求中
        const currentUser = getCurrentUser();
        requestBody.userId = currentUser?.id || 0;

        const { fullResponse, chargeSession } = await requestModelByModeConfig(requestBody, onChunkReceived);

        Logger.debug("解题分析API响应完成", {
          responseLength: fullResponse.length,
        });
        Logger.info("解题分析完成", { responseLength: fullResponse.length });

        // 调用完成回调，传递解题内容和扣费会话
        await onComplete(fullResponse, chargeSession);

        Logger.logApiCall(
          `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
          { prompt, imageCount: imageFilesArr.length, mode: "solve" },
          "success",
          { responseLength: fullResponse.length }
        );
      } catch (error) {
        Logger.error("解题分析失败", error);
        Logger.logApiCall(
          `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
          { prompt, imageCount: imageFilesArr.length, mode: "solve" },
          "error",
          { error: error.message }
        );
        const msg = error?.message?.includes("当前使用人数过多") ? "当前使用人数过多" : (error?.message || "解题分析失败");
        onError(msg);
      }
    });
  } catch (error) {
    Logger.error("解题请求队列处理失败", error);
    const msg = error?.message?.includes("当前使用人数过多") ? "当前使用人数过多" : `请求队列处理失败: ${error?.message || error}`;
    onError(msg);
  }
};

/**
 * 锤锤解题模式 - 第二阶段：生成讲解视频的Manim代码
 * @param {string} originalQuestion 原始题目
 * @param {string} solutionText 解题分析文本
 * @param {Function} onChunkReceived 接收流式响应的回调函数
 * @param {Function} onComplete 完成时的回调函数
 * @param {Function} onError 错误处理回调函数
 * @param {File|File[]} imageFiles 可选的题目图片文件（最多3张）
 * @param {boolean} isRegeneration 是否为重新生成，默认为false
 * @param {boolean|Object} voiceover 是否添加语音旁白，默认为false
 * @param {string} voiceId 选择的音色ID
 * @param {string} targetLang 选定的语言代码 (如 en, ja 等)
 */
export const streamSolveQuestionVideo = async (
  originalQuestion,
  solutionText,
  onChunkReceived,
  onComplete,
  onError,
  imageFiles,
  isRegeneration = false,
  voiceover = false,
  voiceId = null,
  targetLang = null
) => {
  const imageFilesArr = Array.isArray(imageFiles) ? imageFiles : (imageFiles ? [imageFiles] : []);

  // 稳健处理 voiceover 可能被传入对象的情况
  let actualVoiceover = false;
  let actualVoiceId = voiceId;
  let actualTargetLang = targetLang;
  if (typeof voiceover === "object" && voiceover !== null) {
    actualVoiceover = Boolean(voiceover.wantsVoiceover);
    actualVoiceId = voiceover.voiceId || actualVoiceId;
    actualTargetLang = voiceover.lang || actualTargetLang;
  } else {
    actualVoiceover = Boolean(voiceover);
  }

  Logger.info("开始生成解题讲解视频代码", {
    originalQuestion,
    solutionLength: solutionText.length,
    imageCount: imageFilesArr.length,
    isRegeneration,
    voiceover: actualVoiceover,
    voiceId: actualVoiceId,
    targetLang: actualTargetLang,
  });

  // 将API请求包装到队列中执行
  try {
    await requestManager.enqueue(async () => {
      try {
        // 构建简化的请求体 - 系统提示词和模型参数已移至后端
        const requestBody = {
          mode: "solve_video", // 解题视频生成模式
          originalQuestion: originalQuestion, // 原题目内容
          solutionText: solutionText, // 解题分析内容
          stream: false, // 改为非流式响应
          voiceover: actualVoiceover, // 是否生成带语音旁白的代码（严格布尔值）
        };

        if (actualVoiceId) {
          requestBody.voiceId = actualVoiceId;
        }
        if (actualTargetLang) {
          requestBody.target_lang = actualTargetLang;
        }

        // 如果有图片，将所有图片转为 base64 数组
        if (imageFilesArr.length > 0) {
          const base64Images = await Promise.all(imageFilesArr.map(convertImageToBase64));
          requestBody.images = base64Images;
        }

        Logger.debug("发送解题视频生成API请求", requestBody);

        // 添加用户ID到请求中
        const currentUser = getCurrentUser();
        requestBody.userId = currentUser?.id || 0;

        const { fullResponse, chargeSession } = await requestModelByModeConfig(requestBody, onChunkReceived);

        Logger.debug("解题视频生成API响应完成", {
          responseLength: fullResponse.length,
        });

        // 提取代码部分
        let code = await extractManimCode(fullResponse);
        // 自动转义 LaTeX 文本中未转义的下划线
        code = escapeUnderscoreInLaTeXText(code);

        Logger.info("解题视频代码生成完成", {
          responseLength: fullResponse.length,
          codeLength: code.length,
        });
        onComplete(code, chargeSession);

        Logger.logApiCall(
          `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
          { originalQuestion, mode: "solve_video" },
          "success",
          { codeLength: code.length }
        );
      } catch (error) {
        Logger.error("解题视频生成失败", error);
        Logger.logApiCall(
          `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
          { originalQuestion, mode: "solve_video" },
          "error",
          { error: error.message }
        );
        onError(error.message);
      }
    });
  } catch (error) {
    Logger.error("解题视频请求队列处理失败", error);
    onError(`请求队列处理失败: ${error.message}`);
  }
};

export default {
  streamSolveQuestion,
  streamSolveQuestionVideo,
};
